import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { createClient } from '@supabase/supabase-js';

const COLORS = {
  primary: '#2D5016',
  primaryLight: '#4A7C2B',
  secondary: '#8BA888',
  background: '#F5F7F3',
  text: '#2D3E1F',
  textLight: '#6B7F5C',
  border: '#D4E0CE',
  white: '#FFFFFF',
  error: '#C44536',
  success: '#4A7C2B',
};

const SUPABASE_URL = 'https://ztbeipyycwswknnzqyze.supabase.co';
const SUPABASE_ANON_KEY = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6Inp0YmVpcHl5Y3dzd2tubnpxeXplIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NjczMDIyNTMsImV4cCI6MjA4Mjg3ODI1M30.IeuNGgwtGZbcSF-SvbjvR4mDs82u4FW-X2F73diuiYY';

const supabase = createClient(SUPABASE_URL, SUPABASE_ANON_KEY);

const Dashboard = () => {
  const navigate = useNavigate();
  const [couple, setCouple] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [newDropTime, setNewDropTime] = useState('');
  const [updatingTime, setUpdatingTime] = useState(false);

  useEffect(() => {
    loadDashboard();
  }, []);

  const loadDashboard = async () => {
    try {
      // Wait for session to be fully loaded
      const { data: { session }, error: sessionError } = await supabase.auth.getSession();
      
      if (sessionError || !session) {
        console.log('No session found, redirecting to login');
        navigate('/login');
        return;
      }

      // Get user from session
      const user = session.user;
      console.log('Looking for user with email:', user.email);

      // First, find the user record by email
      const { data: userData, error: userError } = await supabase
        .from('users')
        .select('id, email, first_name, last_name')
        .eq('email', user.email)
        .maybeSingle();

      console.log('User query result:', userData, userError);

      if (userError || !userData) {
        console.error('User not found:', userError);
        setError('User account not found');
        setLoading(false);
        return;
      }

      // Now find the couple where this user is either user_a or user_b
      const { data: coupleData, error: coupleError } = await supabase
        .from('couples')
        .select('*')
        .or(`user_a_id.eq.${userData.id},user_b_id.eq.${userData.id}`)
        .maybeSingle();

      if (coupleError || !coupleData) {
        console.error('Couple not found:', coupleError);
        setError('No couple account found');
        setLoading(false);
        return;
      }

      // Fetch both partner names
      const { data: userA } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', coupleData.user_a_id)
        .single();

      const { data: userB } = await supabase
        .from('users')
        .select('first_name, last_name')
        .eq('id', coupleData.user_b_id)
        .single();

      // Map to match what the UI expects
      const mappedCouple = {
        id: coupleData.id,
        email: user.email,
        drop_time: coupleData.drop_time,
        subscription_tier: coupleData.subscription_tier,
        stripe_customer_id: coupleData.stripe_customer_id,
        stripe_subscription_id: coupleData.stripe_subscription_id,
        subscription_status: coupleData.subscription_status,
        user_a: {
          first_name: userA?.first_name || '',
          last_name: userA?.last_name || '',
        },
        user_b: {
          first_name: userB?.first_name || '',
          last_name: userB?.last_name || '',
        },
      };

      setCouple(mappedCouple);
      setNewDropTime(coupleData.drop_time || '19:00:00');
    } catch (err) {
      console.error('Dashboard error:', err);
      setError('Failed to load dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  const handleUpdateDropTime = async () => {
    if (!newDropTime) {
      setError('Please select a drop time');
      return;
    }

    setUpdatingTime(true);
    setError('');
    setSuccess('');

    try {
      const { error: updateError } = await supabase
        .from('couples')
        .update({ drop_time: newDropTime })
        .eq('id', couple.id);

      if (updateError) throw updateError;

      setSuccess('Drop time updated successfully!');
      // Update local state
      setCouple({ ...couple, drop_time: newDropTime });
    } catch (err) {
      console.error('Update error:', err);
      setError('Failed to update drop time');
    } finally {
      setUpdatingTime(false);
    }
  };

  const handleManagePayment = async () => {
    try {
      console.log('[Dashboard] couple object:', couple);
      console.log('[Dashboard] stripe_customer_id:', couple.stripe_customer_id);
      
      if (!couple.stripe_customer_id) {
        setError('No payment method on file. Please contact support.');
        return;
      }
      
      // Call Stripe to create a billing portal session
      const response = await fetch(`${SUPABASE_URL}/functions/v1/create-billing-portal`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session.access_token}`,
        },
        body: JSON.stringify({
          couple_id: couple.id,
          stripe_customer_id: couple.stripe_customer_id,
        }),
      });

      console.log('[Dashboard] Response status:', response.status);
      
      if (!response.ok) {
        const errorData = await response.json();
        console.error('[Dashboard] Error response:', errorData);
        throw new Error(errorData.error || 'Failed to create billing portal session');
      }

      const { url } = await response.json();
      console.log('[Dashboard] Redirecting to:', url);
      // Redirect to Stripe billing portal
      window.location.href = url;
    } catch (err) {
      console.error('Payment management error:', err);
      setError('Failed to open payment management. Please contact support@virbal.net');
    }
  };

  const handleCancelAccount = async () => {
    if (!window.confirm('Are you sure you want to cancel your account? This will delete all your data and cannot be undone.')) {
      return;
    }

    if (!window.confirm('This is permanent. Are you absolutely sure?')) {
      return;
    }

    try {
      // Call edge function to cancel account
      const { data: { user } } = await supabase.auth.getUser();
      
      const response = await fetch(`${SUPABASE_URL}/functions/v1/cancel-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${(await supabase.auth.getSession()).data.session.access_token}`,
        },
        body: JSON.stringify({ couple_id: couple.id }),
      });

      if (!response.ok) throw new Error('Failed to cancel account');

      alert('Your account has been cancelled. You will now be logged out.');
      await supabase.auth.signOut();
      navigate('/');
    } catch (err) {
      console.error('Cancel error:', err);
      setError('Failed to cancel account. Please contact support@virbal.net');
    }
  };

  if (loading) {
    return (
      <div style={{
        minHeight: '100vh',
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
      }}>
        <div style={{
          width: '60px',
          height: '60px',
          border: `4px solid ${COLORS.border}`,
          borderTop: `4px solid ${COLORS.primary}`,
          borderRadius: '50%',
          animation: 'spin 1s linear infinite',
        }}></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    );
  }

  if (!couple) {
    return (
      <div style={{
        minHeight: '100vh',
        background: COLORS.background,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '2rem',
      }}>
        <div style={{ textAlign: 'center' }}>
          <h2 style={{ color: COLORS.error, marginBottom: '1rem' }}>Account Not Found</h2>
          <button
            onClick={() => navigate('/login')}
            style={{
              padding: '0.75rem 1.5rem',
              background: COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
            }}
          >
            Back to Login
          </button>
        </div>
      </div>
    );
  }

  const userA = Array.isArray(couple.user_a) ? couple.user_a[0] : couple.user_a;
  const userB = Array.isArray(couple.user_b) ? couple.user_b[0] : couple.user_b;

  return (
    <div style={{
      minHeight: '100vh',
      background: COLORS.background,
      fontFamily: 'Georgia, serif',
    }}>
      {/* Header */}
      <div style={{
        background: COLORS.white,
        padding: '1.5rem 2rem',
        boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
      }}>
        <img 
          src="https://static.wixstatic.com/media/e78d43_e6a2a4d725014972851fe9dd56aeaa4b~mv2.png"
          alt="Virbal"
          style={{ height: '40px' }}
        />
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <a
            href="https://www.virbal.net"
            style={{
              color: COLORS.primary,
              textDecoration: 'none',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
              fontSize: '0.95rem',
            }}
          >
            ← Back to Virbal
          </a>
          <button
            onClick={handleLogout}
            style={{
              padding: '0.5rem 1.5rem',
              background: 'transparent',
              color: COLORS.primary,
              border: `2px solid ${COLORS.primary}`,
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            Log Out
          </button>
        </div>
      </div>

      {/* Main Content */}
      <div style={{
        maxWidth: '800px',
        margin: '3rem auto',
        padding: '0 2rem',
      }}>
        <h1 style={{
          fontSize: '2.5rem',
          color: COLORS.text,
          marginBottom: '0.5rem',
          fontWeight: 400,
        }}>
          Welcome back!
        </h1>
        <p style={{
          fontSize: '1.2rem',
          color: COLORS.textLight,
          fontFamily: 'Inter, sans-serif',
          marginBottom: '3rem',
        }}>
          {userA.first_name} & {userB.first_name}
        </p>

        {/* Error/Success Messages */}
        {error && (
          <div style={{
            background: '#FFF5F5',
            border: '1px solid #FED7D7',
            color: COLORS.error,
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            fontFamily: 'Inter, sans-serif',
          }}>
            {error}
          </div>
        )}

        {success && (
          <div style={{
            background: 'rgba(74, 124, 43, 0.1)',
            border: '1px solid rgba(74, 124, 43, 0.3)',
            color: COLORS.success,
            padding: '1rem',
            borderRadius: '6px',
            marginBottom: '1.5rem',
            fontFamily: 'Inter, sans-serif',
          }}>
            {success}
          </div>
        )}

        {/* Account Info Card */}
        <div style={{
          background: COLORS.white,
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            color: COLORS.text,
            marginBottom: '1.5rem',
            fontWeight: 400,
          }}>
            Account Information
          </h2>

          <div style={{
            display: 'grid',
            gap: '1rem',
            fontFamily: 'Inter, sans-serif',
          }}>
            <div>
              <p style={{ color: COLORS.textLight, fontSize: '0.9rem', marginBottom: '0.25rem' }}>Partners</p>
              <p style={{ color: COLORS.text, fontSize: '1.1rem' }}>{userA.first_name} {userA.last_name} & {userB.first_name} {userB.last_name}</p>
            </div>

            <div>
              <p style={{ color: COLORS.textLight, fontSize: '0.9rem', marginBottom: '0.25rem' }}>Email</p>
              <p style={{ color: COLORS.text, fontSize: '1.1rem' }}>{couple.email}</p>
            </div>

            <div>
              <p style={{ color: COLORS.textLight, fontSize: '0.9rem', marginBottom: '0.25rem' }}>Plan</p>
              <p style={{ color: COLORS.text, fontSize: '1.1rem', textTransform: 'capitalize' }}>
                {couple.subscription_tier || 'Standard'} - $8/month
              </p>
            </div>

            <div>
              <p style={{ color: COLORS.textLight, fontSize: '0.9rem', marginBottom: '0.25rem' }}>Current Drop Time</p>
              <p style={{ color: COLORS.text, fontSize: '1.1rem' }}>
                {couple.drop_time ? new Date(`2000-01-01T${couple.drop_time}`).toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' }) : 'Not set'}
              </p>
            </div>
          </div>
        </div>

        {/* Update Drop Time Card */}
        <div style={{
          background: COLORS.white,
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            color: COLORS.text,
            marginBottom: '1.5rem',
            fontWeight: 400,
          }}>
            Update Drop Time
          </h2>

          <div style={{ display: 'flex', gap: '1rem', alignItems: 'flex-end' }}>
            <div style={{ flex: 1 }}>
              <label style={{
                display: 'block',
                fontSize: '0.9rem',
                color: COLORS.text,
                marginBottom: '0.5rem',
                fontFamily: 'Inter, sans-serif',
              }}>New Drop Time</label>
              <select
                value={newDropTime.split(':')[0]}
                onChange={(e) => setNewDropTime(`${e.target.value}:00:00`)}
                style={{
                  width: '100%',
                  padding: '0.75rem',
                  border: `2px solid ${COLORS.border}`,
                  borderRadius: '6px',
                  fontSize: '1rem',
                  fontFamily: 'Inter, sans-serif',
                }}
              >
                <option value="08">8 AM</option>
                <option value="09">9 AM</option>
                <option value="10">10 AM</option>
                <option value="11">11 AM</option>
                <option value="12">12 PM</option>
                <option value="13">1 PM</option>
                <option value="14">2 PM</option>
                <option value="15">3 PM</option>
                <option value="16">4 PM</option>
                <option value="17">5 PM</option>
                <option value="18">6 PM</option>
                <option value="19">7 PM</option>
                <option value="20">8 PM</option>
              </select>
            </div>
            <button
              onClick={handleUpdateDropTime}
              disabled={updatingTime}
              style={{
                padding: '0.75rem 2rem',
                background: COLORS.primary,
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: updatingTime ? 'not-allowed' : 'pointer',
                fontFamily: 'Inter, sans-serif',
                fontWeight: 600,
                opacity: updatingTime ? 0.6 : 1,
              }}
            >
              {updatingTime ? 'Updating...' : 'Update'}
            </button>
          </div>
        </div>

        {/* Payment Management Card */}
        <div style={{
          background: COLORS.white,
          borderRadius: '8px',
          padding: '2rem',
          marginBottom: '2rem',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            color: COLORS.text,
            marginBottom: '1rem',
            fontWeight: 400,
          }}>
            Payment & Billing
          </h2>
          
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          }}>
            Manage your payment method, view invoices, and update billing information.
          </p>

          <button
            onClick={handleManagePayment}
            style={{
              padding: '0.75rem 2rem',
              background: COLORS.primary,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            Manage Payment Method
          </button>
        </div>

        {/* Cancel Account Card */}
        <div style={{
          background: COLORS.white,
          borderRadius: '8px',
          padding: '2rem',
          boxShadow: '0 2px 8px rgba(45, 80, 22, 0.08)',
        }}>
          <h2 style={{
            fontSize: '1.5rem',
            color: COLORS.error,
            marginBottom: '1rem',
            fontWeight: 400,
          }}>
            Danger Zone
          </h2>
          <p style={{
            color: COLORS.textLight,
            fontFamily: 'Inter, sans-serif',
            marginBottom: '1.5rem',
            lineHeight: 1.6,
          }}>
            Canceling your account will delete all your data and cannot be undone. You will stop receiving daily drops and recaps.
          </p>
          <button
            onClick={handleCancelAccount}
            style={{
              padding: '0.75rem 2rem',
              background: COLORS.error,
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              cursor: 'pointer',
              fontFamily: 'Inter, sans-serif',
              fontWeight: 600,
            }}
          >
            Cancel Account
          </button>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;
